import { TargetSchema, ResourceName } from "@/types";
import { CircularProgress, Typography } from "@mui/material";
import { useGetOne } from "react-admin";

/**
 * Displays a clickable link to an entity record with human-readable label
 * Fetches the actual record to show meaningful names instead of ObjectIDs
 */
export const EntityLink: React.FC<{
  entityId: string;
  targetSchema: string;
}> = ({ entityId, targetSchema }) => {
  // Maps schema names to their React Admin resources and display fields
  const getResourceConfig = (targetSchema: string) => {
    switch (targetSchema) {
      case TargetSchema.POLICY:
      case ResourceName.POLICIES:
        return {
          resource: ResourceName.POLICIES,
          labelField: "aegle_id", // Shows: "POL-2024-001234"
          path: ResourceName.POLICIES,
        };
      case TargetSchema.CLAIMANT:
      case ResourceName.CLAIMANTS:
        return {
          resource: ResourceName.CLAIMANTS,
          labelField: "claimant_name", // Shows: "John Smith"
          path: ResourceName.CLAIMANTS,
        };
      case TargetSchema.CLIENT:
      case ResourceName.CLIENTS:
        return {
          resource: ResourceName.CLIENTS,
          labelField: "client_legal_name", // Shows: "ACME Corporation"
          path: ResourceName.CLIENTS,
        };
      case TargetSchema.STOPLOSS_CLAIM:
      case ResourceName.STOPLOSS_CLAIMS:
        return {
          resource: ResourceName.STOPLOSS_CLAIMS,
          labelField: "aegle_id", // Shows: "SL-2024-005678"
          path: ResourceName.STOPLOSS_CLAIMS,
        };
      default:
        return {
          resource: targetSchema,
          labelField: "id", // Fallback to ObjectID
          path: targetSchema,
        };
    }
  };

  const config = getResourceConfig(targetSchema);

  // Fetch the actual record to get display-friendly labels (cached by React Admin)
  const { data: record, isLoading, error } = useGetOne(config.resource, { id: entityId }, { enabled: !!entityId });

  // Show spinner while loading
  if (isLoading) {
    return <CircularProgress size={16} />;
  }

  // Fallback to ObjectID if record fetch fails
  if (error || !record) {
    return (
      <Typography variant="body2" color="text.secondary" title={`Failed to load ${targetSchema} record`}>
        {entityId}
      </Typography>
    );
  }

  const displayLabel = record[config.labelField] || entityId;

  return (
    <Typography
      variant="body2"
      color="primary.main"
      sx={{
        cursor: "pointer",
        textDecoration: "underline",
        "&:hover": {
          textDecoration: "none",
        },
      }}
      onClick={() => {
        // Open record details in new tab
        window.open(`#/${config.path}/${entityId}/show`, "_blank");
      }}
      title={`View ${targetSchema}: ${displayLabel}`}
    >
      {displayLabel}
    </Typography>
  );
};
